import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail, downloadReport } from "@/lib/api";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, FileDown, FileSpreadsheet, Search, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", address: "", aadhaar: "", mobile: "", parent_mobile: "",
  salary: "", salary_type: "monthly", licence_number: "", photo: "", aadhaar_doc: "", documents: [],
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function Employees() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const { data: employees = [] } = useQuery({
    queryKey: ["employees", q],
    queryFn: async () => (await api.get("/employees", { params: { q: q || undefined } })).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, salary: Number(form.salary || 0) };
      return editId ? api.put(`/employees/${editId}`, payload) : api.post("/employees", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(editId ? "Employee updated" : "Employee added");
      setOpen(false); setForm(empty); setEditId(null);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/employees/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employees"] }); toast.success("Deleted"); },
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const data = await fileToBase64(f);
    setForm((s) => ({ ...s, photo: data }));
  };
  const onAadhaar = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const data = await fileToBase64(f);
    setForm((s) => ({ ...s, aadhaar_doc: data }));
  };
  const onDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    const docs = await Promise.all(files.map(async (f) => ({ name: f.name, data: await fileToBase64(f) })));
    setForm((s) => ({ ...s, documents: [...s.documents, ...docs] }));
  };

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (emp) => {
    setForm({ name: emp.name, address: emp.address, aadhaar: emp.aadhaar, mobile: emp.mobile,
      parent_mobile: emp.parent_mobile, salary: emp.salary, salary_type: emp.salary_type,
      licence_number: emp.licence_number, photo: emp.photo || "", aadhaar_doc: emp.aadhaar_doc || "", documents: emp.documents || [] });
    setEditId(emp.id); setOpen(true);
  };

  const aadhaarValid = !form.aadhaar || /^\d{12}$/.test(form.aadhaar);

  return (
    <div>
      <PageTitle title="Employee Details" subtitle={`${employees.length} employees`}>
        <Button variant="outline" onClick={() => downloadReport("/reports/employees/export", { fmt: "pdf" }, "employees")}><FileDown className="h-4 w-4" /> PDF</Button>
        <Button variant="outline" onClick={() => downloadReport("/reports/employees/export", { fmt: "excel" }, "employees")}><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
        {isAdmin && <Button onClick={openNew} data-testid="add-employee-button"><Plus className="h-4 w-4" /> Add Employee</Button>}
      </PageTitle>

      <Card className="p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search name, emp no, mobile…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="employee-search" />
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground col-span-full">No employees yet.</Card>
        ) : employees.map((emp) => (
          <Card key={emp.id} className="p-5 card-elevate" data-testid={`employee-card-${emp.employee_no}`}>
            <div className="flex items-start gap-4">
              {emp.photo ? (
                <img src={emp.photo} alt={emp.name} className="h-16 w-16 rounded-xl object-cover border" />
              ) : (
                <div className="grid h-16 w-16 place-items-center rounded-xl bg-primary/10 text-primary font-display text-xl font-bold">{emp.name.charAt(0)}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{emp.name}</div>
                <div className="text-xs text-primary font-medium">{emp.employee_no}</div>
                <Badge variant="secondary" className="mt-1 capitalize">{emp.salary_type}</Badge>
              </div>
            </div>
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Salary</span><span className="data-num font-medium">{inr(emp.salary)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Mobile</span><span>{emp.mobile || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Parent</span><span>{emp.parent_mobile || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Aadhaar</span><span>{emp.aadhaar || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Licence</span><span>{emp.licence_number || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Documents</span><span>{(emp.documents?.length || 0) + (emp.aadhaar_doc ? 1 : 0)}</span></div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(emp)} data-testid={`edit-emp-${emp.employee_no}`}><Pencil className="h-4 w-4" /> {isAdmin ? "Edit" : "View"}</Button>
              {isAdmin && <AlertDialog>
                <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete {emp.name}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del.mutate(emp.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto ssacc-scroll">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Employee" : "Add Employee"}</DialogTitle>
            <DialogDescription>Employee number is generated automatically. Aadhaar must be 12 digits.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="emp-name" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={set("mobile")} className="mt-1.5" data-testid="emp-mobile" /></div>
            <div><Label>Parent Mobile</Label><Input value={form.parent_mobile} onChange={set("parent_mobile")} className="mt-1.5" data-testid="emp-parent" /></div>
            <div>
              <Label>Aadhaar (12 digits)</Label>
              <Input value={form.aadhaar} onChange={set("aadhaar")} maxLength={12} className={`mt-1.5 ${!aadhaarValid ? "border-destructive" : ""}`} data-testid="emp-aadhaar" />
              {!aadhaarValid && <p className="text-xs text-destructive mt-1">Must be exactly 12 digits</p>}
            </div>
            <div><Label>Licence Number</Label><Input value={form.licence_number} onChange={set("licence_number")} className="mt-1.5" data-testid="emp-licence" /></div>
            <div><Label>Salary (₹)</Label><Input type="number" value={form.salary} onChange={set("salary")} className="mt-1.5" data-testid="emp-salary" /></div>
            <div>
              <Label>Salary Type</Label>
              <Select value={form.salary_type} onValueChange={(v) => setForm((f) => ({ ...f, salary_type: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="emp-salary-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={set("address")} className="mt-1.5" data-testid="emp-address" /></div>

            <div>
              <Label>Photo</Label>
              <div className="mt-1.5 flex items-center gap-3">
                {form.photo && <img src={form.photo} alt="" className="h-14 w-14 rounded-lg object-cover border" />}
                <label className="flex items-center gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer hover:bg-muted"><Upload className="h-4 w-4" /> Upload<input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="emp-photo" /></label>
              </div>
            </div>
            <div>
              <Label>Aadhaar Document</Label>
              <div className="mt-1.5 flex items-center gap-3">
                {form.aadhaar_doc && <Badge variant="secondary"><FileText className="h-3 w-3 mr-1" /> Attached</Badge>}
                <label className="flex items-center gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer hover:bg-muted"><Upload className="h-4 w-4" /> Upload<input type="file" className="hidden" onChange={onAadhaar} data-testid="emp-aadhaar-doc" /></label>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Other Documents</Label>
              <div className="mt-1.5">
                <label className="flex items-center gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer hover:bg-muted w-fit"><Upload className="h-4 w-4" /> Add files<input type="file" multiple className="hidden" onChange={onDocs} data-testid="emp-docs" /></label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.documents.map((d, i) => (
                    <Badge key={i} variant="outline" className="gap-1">{d.name}<button onClick={() => setForm((f) => ({ ...f, documents: f.documents.filter((_, idx) => idx !== i) }))}><X className="h-3 w-3" /></button></Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            {isAdmin && <Button onClick={() => save.mutate()} disabled={!form.name || !aadhaarValid || save.isPending} data-testid="save-employee">{save.isPending ? "Saving…" : "Save"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
